import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { jornadaAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Clock, LogIn, Coffee, Pause, LogOut as LogOutIcon, Users, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const Jornada = () => {
  const { user } = useAuth();
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [registrosAdmin, setRegistrosAdmin] = useState([]);
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  const isAdmin = user?.perfil === 'ADMIN';

  useEffect(() => {
    loadRegistros();
    if (isAdmin) {
      loadUsuarios();
    }
  }, [isAdmin]);

  const loadRegistros = async () => {
    try {
      const response = await jornadaAPI.minha();
      setRegistros(response.data);
    } catch (error) {
      console.error('Erro ao carregar jornada:', error);
    }
  };

  const loadUsuarios = async () => {
    try {
      const response = await usersAPI.list();
      // Filtrar apenas vendedores ativos
      const vendedores = response.data.filter(u => u.perfil === 'VENDEDOR' && u.ativo);
      setUsuarios(vendedores);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadRegistrosAdmin = async () => {
    if (!selectedUserId) {
      toast.error('Selecione um vendedor');
      return;
    }

    setLoadingAdmin(true);
    try {
      const response = await jornadaAPI.todas({ 
        user_id: selectedUserId,
        data: selectedDate
      });
      setRegistrosAdmin(response.data);
    } catch (error) {
      toast.error('Erro ao carregar registros');
      console.error('Erro:', error);
    } finally {
      setLoadingAdmin(false);
    }
  };

  const registrarPonto = async (tipo) => {
    setLoading(true);
    try {
      await jornadaAPI.registrar({ tipo });
      toast.success(`${tipo} registrado com sucesso!`);
      loadRegistros();
    } catch (error) {
      toast.error('Erro ao registrar ponto');
    } finally {
      setLoading(false);
    }
  };

  const tiposPonto = [
    { tipo: 'Entrada', icon: LogIn, color: 'bg-green-500', label: 'Entrada' },
    { tipo: 'Almoço - Início', icon: Coffee, color: 'bg-orange-500', label: 'Início Almoço' },
    { tipo: 'Almoço - Fim', icon: Coffee, color: 'bg-orange-500', label: 'Fim Almoço' },
    { tipo: 'Intervalo - Início', icon: Pause, color: 'bg-blue-500', label: 'Início Intervalo' },
    { tipo: 'Intervalo - Fim', icon: Pause, color: 'bg-blue-500', label: 'Fim Intervalo' },
    { tipo: 'Saída', icon: LogOutIcon, color: 'bg-red-500', label: 'Saída' },
  ];

  // Agrupar registros por tipo para exibição resumida
  const getRegistroPorTipo = (registros, tipo) => {
    const registro = registros.find(r => r.tipo === tipo);
    return registro ? format(new Date(registro.timestamp), 'HH:mm') : '-';
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Controle de Jornada</h1>
        <p className="text-gray-500 mt-1">Registre seus horários de trabalho</p>
      </div>

      {/* Botões de Registro - Para todos os usuários */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Registrar Ponto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {tiposPonto.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.tipo}
                  onClick={() => registrarPonto(item.tipo)}
                  disabled={loading}
                  className={`${item.color} hover:opacity-90 h-20 flex flex-col gap-2`}
                >
                  <Icon className="h-6 w-6" />
                  <span className="text-xs">{item.label}</span>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Meus Registros de Hoje */}
      <Card>
        <CardHeader>
          <CardTitle>Meus Registros de Hoje</CardTitle>
        </CardHeader>
        <CardContent>
          {registros.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhum registro hoje</p>
          ) : (
            <div className="space-y-2">
              {registros.map((registro) => (
                <div
                  key={registro.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="font-medium">{registro.tipo}</p>
                      <p className="text-sm text-gray-500">
                        {format(new Date(registro.timestamp), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Painel Admin - Visualizar Jornada dos Vendedores */}
      {isAdmin && (
        <Card className="border-2 border-blue-200">
          <CardHeader className="bg-blue-50">
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Users className="h-5 w-5" />
              Jornada dos Vendedores (Admin)
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label>Vendedor</Label>
                <Select
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um vendedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {usuarios.map((usuario) => (
                      <SelectItem key={usuario.id} value={usuario.id}>
                        {usuario.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Data</Label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <Button 
                  onClick={loadRegistrosAdmin}
                  disabled={loadingAdmin}
                  className="w-full"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  {loadingAdmin ? 'Carregando...' : 'Consultar'}
                </Button>
              </div>
            </div>

            {/* Resultado da Consulta */}
            {registrosAdmin.length > 0 ? (
              <div className="space-y-4">
                {/* Resumo Visual */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  <div className="p-4 bg-green-50 rounded-lg text-center border border-green-200">
                    <LogIn className="h-6 w-6 mx-auto text-green-600 mb-2" />
                    <p className="text-xs text-gray-600">Entrada</p>
                    <p className="font-bold text-green-700">{getRegistroPorTipo(registrosAdmin, 'Entrada')}</p>
                  </div>
                  
                  <div className="p-4 bg-orange-50 rounded-lg text-center border border-orange-200">
                    <Coffee className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                    <p className="text-xs text-gray-600">Almoço Início</p>
                    <p className="font-bold text-orange-700">{getRegistroPorTipo(registrosAdmin, 'Almoço - Início')}</p>
                  </div>
                  
                  <div className="p-4 bg-orange-50 rounded-lg text-center border border-orange-200">
                    <Coffee className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                    <p className="text-xs text-gray-600">Almoço Fim</p>
                    <p className="font-bold text-orange-700">{getRegistroPorTipo(registrosAdmin, 'Almoço - Fim')}</p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg text-center border border-blue-200">
                    <Pause className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                    <p className="text-xs text-gray-600">Intervalo Início</p>
                    <p className="font-bold text-blue-700">{getRegistroPorTipo(registrosAdmin, 'Intervalo - Início')}</p>
                  </div>
                  
                  <div className="p-4 bg-blue-50 rounded-lg text-center border border-blue-200">
                    <Pause className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                    <p className="text-xs text-gray-600">Intervalo Fim</p>
                    <p className="font-bold text-blue-700">{getRegistroPorTipo(registrosAdmin, 'Intervalo - Fim')}</p>
                  </div>
                  
                  <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                    <LogOutIcon className="h-6 w-6 mx-auto text-red-600 mb-2" />
                    <p className="text-xs text-gray-600">Saída</p>
                    <p className="font-bold text-red-700">{getRegistroPorTipo(registrosAdmin, 'Saída')}</p>
                  </div>
                </div>

                {/* Lista Detalhada */}
                <div className="mt-4">
                  <h4 className="font-semibold text-gray-700 mb-2">Registros Detalhados</h4>
                  <div className="space-y-2">
                    {registrosAdmin.map((registro) => (
                      <div
                        key={registro.id}
                        className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <Clock className="h-5 w-5 text-gray-400" />
                          <div>
                            <p className="font-medium">{registro.tipo}</p>
                            <p className="text-sm text-gray-500">
                              {format(new Date(registro.timestamp), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
                            </p>
                          </div>
                        </div>
                        <span className="text-sm text-gray-500">{registro.user_nome}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : selectedUserId && registrosAdmin.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Nenhum registro encontrado para este vendedor nesta data</p>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>Selecione um vendedor e uma data para consultar</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Jornada;
